<script setup lang="ts">
/**
 * AppHeader — unified sticky page header for every dashboard view.
 *
 * Extracted from HomeView (which is the reference implementation): wordmark
 * row (LUCKYWAR link home + user name + logout) with AppNav directly below,
 * all inside one sticky, safe-area-aware header.
 *
 * App.vue renders this above the routed dashboard view so every page shares
 * the same top chrome. Public routes (login/pending/revoked) do not render it
 * because they have no viewer session.
 */
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import AppNav from '@/components/AppNav.vue';
import BranchSearch from '@/components/BranchSearch.vue';

const router = useRouter();
const { displayName, logout } = useAuth();

async function onLogout() {
  await logout();
  router.push({ name: 'login' });
}
</script>

<template>
  <header class="app-header">
    <div class="app-header__top">
      <RouterLink to="/" class="app-header__brand">
        <span class="app-header__wordmark">LUCKY<span class="app-header__wordmark-accent">WAR</span></span>
      </RouterLink>
      <BranchSearch class="app-header__search" />
      <div class="app-header__user">
        <span class="app-header__user-name">{{ displayName }}</span>
        <button class="app-header__logout-btn" @click="onLogout">ออกจากระบบ</button>
      </div>
    </div>
    <AppNav />
  </header>
</template>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  padding: 0.65rem 1rem;
  padding-top: calc(0.65rem + env(safe-area-inset-top));
  /* Sticky chrome uses the 1px border, not elevation. */
}

.app-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.app-header__brand {
  display: inline-flex;
  align-items: baseline;
}

.app-header__wordmark {
  font-family: 'Kanit', 'Sarabun', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.06em;
  color: var(--text-dim);
  line-height: 1;
}
.app-header__wordmark-accent {
  color: var(--brand-light);
  font-weight: 700;
}

/* Header search: centered between wordmark and user block, shrinkable. */
.app-header__search {
  flex: 1 1 auto;
  min-width: 0;
}

.app-header__user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.app-header__user-name {
  font-size: 0.78rem;
  color: var(--text-dim);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 8rem;
}

.app-header__logout-btn {
  border: 1px solid var(--line-light);
  border-radius: var(--radius-chip);
  padding: 0.35rem 0.65rem;
  background: transparent;
  color: var(--text-dim);
  font-weight: 600;
  font-size: 0.7rem;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.app-header__logout-btn:hover {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--line);
}
</style>
